import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton } from '@mui/material';
import { useFormik } from 'formik';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import Fade from 'react-reveal/Fade';
import { useHistory } from 'react-router';
import Swal from 'sweetalert2';
import { changeMember, searchUserByCitizenId } from '../../../../api/myCompany';
import {
    getAllRequestedServicesByCompanyIDWithFilters,
    getRequestedServicesCitizenIDWithAllCompanyMembers
} from '../../../../api/RequestedServiceList';
import CenterLoading from '../../../../components/CenterLoading/CenterLoading';
import RequestCard from '../../../../components/RequestCard/RequestCard';
import Select from '../../../../components/Select/Select';
import TextField from '../../../../components/TextField/TextField';
import COLORS from '../../../../theme/Colors';
import FONTS_FAMILY from '../../../../theme/FontsFamily';
import { InputFilter, MediumHeightDivider, Row, RowMedia, SmallHeightDivider, StyledPagination } from '../../../../theme/Styles';
import { requestFilterTypes } from '../../MyDeskConstants';
import { ListContainer, SectionTitle, RequestsWrapper } from '../../styles/MyDeskStyles';
import { FormCompanySendInviteSchema } from '../Company/CompanyConstants';
import DialogChangeApplicant from '../Company/components/DialogChangeApplicant';
import EmptyState from '../../components/EmptyState';
import { replaceGuionToSlashFromString } from '../../../../utilities/functions/StringUtil';

async function fetchMergedRequests(fetchPageFn, targetPage, pageSize, filterFn) {
    try {
        const firstApiRes = await fetchPageFn(1);
        if (!firstApiRes) {
            return { data: [], last_page: 1, total: 0, per_page: pageSize, apiTotal: 0, apiLastPage: 1, apiPagesScanned: 0 };
        }

        const total = firstApiRes?.total !== undefined ? firstApiRes.total : (firstApiRes?.data ? firstApiRes.data.length : 0);
        const apiPageSize = firstApiRes?.per_page || 5;
        const apiLastPage = firstApiRes?.last_page || Math.ceil(total / apiPageSize);

        // Consultar páginas de la API en paralelo (máximo 25 páginas que permite la API para mantener velocidad)
        const pagesToFetch = Math.min(apiLastPage, 25);
        let allData = [];
        if (pagesToFetch > 0) {
            const promises = [];
            for (let p = 1; p <= pagesToFetch; p++) {
                promises.push(fetchPageFn(p).catch(() => null));
            }
            const results = await Promise.all(promises);
            results.forEach(res => {
                if (res && res.data && Array.isArray(res.data)) {
                    allData = allData.concat(res.data);
                }
            });
        }

        // Deduplicar solicitudes por ID único
        const uniqueData = [];
        const seenIds = new Set();
        allData.forEach(item => {
            if (item && item.id && !seenIds.has(item.id)) {
                seenIds.add(item.id);
                uniqueData.push(item);
            }
        });
        
        const filteredData = filterFn ? uniqueData.filter(filterFn) : uniqueData;
        
        // Recalcular la cantidad exacta de páginas según los elementos filtrados y el pageSize (10/25/50)
        const totalMatching = filteredData.length;
        const calculatedLastPage = Math.max(1, Math.ceil(totalMatching / pageSize));
        
        const validPage = Math.max(1, Math.min(targetPage, calculatedLastPage));
        const startIndex = (validPage - 1) * pageSize;
        const slicedData = filteredData.slice(startIndex, startIndex + pageSize);
        
        return {
            data: slicedData,
            last_page: calculatedLastPage,
            total: totalMatching,
            per_page: pageSize,
            // Datos reales que reporta la API, sin importar cuántas páginas se hayan podido consultar
            apiTotal: total,
            apiLastPage: apiLastPage,
            apiPagesScanned: pagesToFetch
        };
    } catch (err) {
        console.error("Error in fetchMergedRequests:", err);
        return { data: [], last_page: 1, total: 0, per_page: pageSize, apiTotal: 0, apiLastPage: 1, apiPagesScanned: 0 };
    }
}

const matchesDateFilter = (req, dateVal) => {
    if (!dateVal) return true;
    const parts = dateVal.split('-');
    if (parts.length !== 3) return true;
    const [year, month, day] = parts;
    const numYear = parseInt(year, 10);
    const numMonth = parseInt(month, 10);
    const numDay = parseInt(day, 10);

    const matchesDate = (dateString) => {
        if (!dateString) return false;
        const d = new Date(replaceGuionToSlashFromString(dateString));
        if (isNaN(d.getTime())) return false;
        return d.getFullYear() === numYear &&
               (d.getMonth() + 1) === numMonth &&
               d.getDate() === numDay;
    };

    return matchesDate(req?.created_at) || matchesDate(req?.updated_at);
};

const isActionRequiredExpired = (req) => {
    const actions = req?.required_actions || req?.requiredActions || (req?.request_actions ? (Array.isArray(req.request_actions) ? req.request_actions : [req.request_actions]) : []);
    if (!Array.isArray(actions) || actions.length === 0) return false;
    const now = new Date();
    return actions.some(act => {
        if (!act) return false;
        const expStr = act.expiration_date || act.limit_date || act.due_date;
        if (expStr) {
            const d = new Date(String(expStr).replace(/-/g, '/'));
            return !isNaN(d.getTime()) && now > d;
        }
        if (act.created_at) {
            const created = new Date(String(act.created_at).replace(/-/g, '/'));
            if (!isNaN(created.getTime())) {
                const limit = new Date(created.getTime() + 10 * 24 * 60 * 60 * 1000);
                return now > limit;
            }
        }
        return false;
    });
};

const filterRequestByStatusType = (req, selectedType, dateFilter = '') => {
    if (dateFilter && !matchesDateFilter(req, dateFilter)) {
        return false;
    }

    const typeNum = Number(selectedType);
    if (typeNum === 100) return true; // Todas las solicitudes

    const statusId = Number(req?.status?.id || req?.status_id);
    const statusName = String(req?.status?.name || '').toLowerCase();

    // Canceladas / Rechazadas / Desestimadas / Vencidas / Expiradas por acción requerida
    const isCanceledOrRejected = statusId === 10 || statusId === 8 || statusId === 9 || statusId === 5 ||
        statusName.includes('cancelad') || statusName.includes('rechazad') || 
        statusName.includes('desestimad') || statusName.includes('vencid') || 
        statusName.includes('expirad') || isActionRequiredExpired(req);

    // Aprobadas / Completadas (Strictly NOT canceled/rejected)
    const isApproved = !isCanceledOrRejected && (
        statusId === 3 || statusName.includes('aprobad') || statusName.includes('completad')
    );

    // Acción Requerida: Notificación u observación del analista (independiente del estatus,
    // incluyendo solicitudes ya Aprobadas/Canceladas/Rechazadas)
    const hasAction = (
        (req?.required_actions && Array.isArray(req.required_actions) && req.required_actions.length > 0) ||
        (req?.requiredActions && Array.isArray(req.requiredActions) && req.requiredActions.length > 0) ||
        (req?.request_actions !== null && req?.request_actions !== undefined)
    );

    // 1. Acción Requerida (99) - Filtro independiente
    if (typeNum === 99) {
        return hasAction;
    }

    // 2. Aprobada (3)
    if (typeNum === 3) {
        return isApproved;
    }

    // 3. Finalizada (7)
    if (typeNum === 7) {
        return isCanceledOrRejected;
    }

    // 4. En proceso (1, 20)
    if (typeNum === 1 || typeNum === 20 || typeNum === 2 || typeNum === 5) {
        return !hasAction && !isApproved && !isCanceledOrRejected;
    }

    return true;
};

const hasRequiredActions = (req) => {
    const statusId = Number(req?.status?.id || req?.status_id);
    const statusName = String(req?.status?.name || '').toLowerCase();
    if (statusId === 10 || statusId === 8 || statusId === 3 || statusName.includes('cancelad') || statusName.includes('rechazad') || statusName.includes('aprobad')) {
        return false;
    }

    const actions = req?.required_actions || req?.requiredActions || (req?.request_actions ? (Array.isArray(req.request_actions) ? req.request_actions : [req.request_actions]) : []);
    if (!actions || actions.length === 0) return false;

    const comments = req?.comments ? [...req.comments] : [];
    comments.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;

    if (lastComment && (lastComment.user_id === null || lastComment.user_id === undefined)) {
        return false;
    }

    const pendingActions = actions.filter(act => {
        const isCompleted = act?.status === true || act?.status === 1 || act?.status === 'true' || act?.completed === true;
        if (isCompleted) return false;
        return true;
    });

    return pendingActions.length > 0;
};

function MyRequests({ rolBase }) {
    // const matchesWidth = useMediaQuery("(min-width:768px)");
    const history = useHistory();
    const initialDataApplicant = {
        value: 0,
        label: 'Todos los solicitantes'
    };
    const finishData = {
        value: 0,
        label: 'Sin registros'
    };
    // const dispatch = useDispatch();
    const { companyID, citizenID } = useSelector((state) => state.authReducer);

    const queryClient = useQueryClient();
    const userData = queryClient.getQueryData(['userData']);
    const [companySelected, setCompanySelected] = useState(null);
    const [requestCode, setRequestCode] = useState();
    const [requestForApplicant, setRequestForApplicant] = useState([]);
    const [requestForApplicantId, setRequestForApplicantId] = useState(0);

    const [requestType, setRequestType] = useState(99); // Por defecto: Acción Requerida
    const [requestName, setRequestName] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [stringFilter, setStringFilter] = useState('');

    const titleRef = useRef(null);

    const [claimModalVisible, setClaimModalVisible] = useState(false);
    const [searchUser, setSearchUser] = useState(null);

    const mutationSearchUserById = useMutation(searchUserByCitizenId);
    const mutationChangeMember = useMutation(changeMember);

    const { data: allStatuses } = useQuery(
        ['allStatuses', companyID, citizenID],
        async () => {
            try {
                const firstPage = await getAllRequestedServicesByCompanyIDWithFilters(companyID, 1, 100, '', 0, citizenID);
                const ids = new Set();
                if (firstPage && firstPage.data) {
                    firstPage.data.forEach(req => {
                        if (req?.status?.id !== undefined && req?.status?.id !== null) {
                            ids.add(Number(req.status.id));
                        }
                        if (hasRequiredActions(req)) {
                            ids.add(99);
                        }
                    });
                }
                const totalPages = Math.min(firstPage?.last_page || 1, 3);
                if (totalPages > 1) {
                    const promises = [];
                    for (let p = 2; p <= totalPages; p++) {
                        promises.push(getAllRequestedServicesByCompanyIDWithFilters(companyID, p, 100, '', 0, citizenID).catch(() => null));
                    }
                    const results = await Promise.all(promises);
                    results.forEach(res => {
                        if (res && res.data) {
                            res.data.forEach(req => {
                                if (req?.status?.id !== undefined && req?.status?.id !== null) {
                                    ids.add(Number(req.status.id));
                                }
                                if (hasRequiredActions(req)) {
                                    ids.add(99);
                                }
                            });
                        }
                    });
                }
                console.log("DEBUG: allStatuses (company view) fetched ids:", Array.from(ids));
                return ids;
            } catch (err) {
                console.error("Error fetching allStatuses (company view):", err);
                throw err;
            }
        },
        {
            enabled: !!companyID,
            staleTime: 60000,
            retry: false
        }
    );

    const { data: requestsList, isLoading: requestListIsLoading } = useQuery(
        ['requestsList', companyID, citizenID, requestType, stringFilter, currentPage, requestForApplicantId, pageSize],
        async () => {
            return fetchMergedRequests(
                (p) => getAllRequestedServicesByCompanyIDWithFilters(companyID, p, 100, stringFilter, requestForApplicantId, citizenID),
                currentPage,
                pageSize,
                (req) => filterRequestByStatusType(req, requestType)
            );
        },
        {
            staleTime: 60000,
            enabled: !!companyID
        }
    );

    const { data: requestListByCompanyMembers, isLoading: requestListByCompanyMembersIsLoading } = useQuery(
        ['requestListByCompanyMembers', citizenID],
        () => getRequestedServicesCitizenIDWithAllCompanyMembers(citizenID),
        {
            staleTime: 60000,
            enabled: !!citizenID
        }
    );

    const totalRequests = requestsList?.total || 0;

    // Fallback: extract status IDs from currently visible requests in the main list
    const visibleStatuses = new Set();
    (requestsList?.data || []).forEach(req => {
        if (req?.status?.id !== undefined && req?.status?.id !== null) {
            visibleStatuses.add(Number(req.status.id));
        }
        if (hasRequiredActions(req)) {
            visibleStatuses.add(99);
        }
    });

    const hasMorePages = Boolean(requestsList && requestsList.apiLastPage > requestsList.apiPagesScanned);
    const emptyTitle = hasMorePages ? "No se encontraron coincidencias en el rango consultado" : "No se encontraron registros";
    const emptySubtitle = hasMorePages
        ? `Solo se consultaron las primeras ${requestsList?.apiPagesScanned} de ${requestsList?.apiLastPage} páginas disponibles en la API (${requestsList?.apiTotal} solicitudes en total). Puede haber solicitudes que coincidan con este filtro más adelante en el historial que aún no se han revisado.`
        : "No existen solicitudes activas que coincidan con sus criterios de búsqueda en este momento.";

    const filteredFilterTypes = requestFilterTypes;

    useEffect(() => {
        if (requestForApplicantId != 0) {
            setCurrentPage(1);
        }
    }, [requestForApplicantId]);

    useEffect(() => {
        if (requestsList?.data?.length > 0 && requestListByCompanyMembers?.payload?.companies?.length > 0) {
            setRequestForApplicant(() => [initialDataApplicant, ...requestListByCompanyMembers?.payload?.members]);
        }
    }, [requestsList]);

    const searchUserById = (formData) => {
        mutationSearchUserById.mutate(formData, {
            onSuccess: (data) => {
                if (data.success) {
                    setSearchUser(data.payload);
                    // refresh cache of searchUserId
                    queryClient.invalidateQueries('searchUserId');
                } else {
                    Swal.fire({
                        title: 'Información',
                        text: data.msg,
                        icon: 'info'
                    });
                }
            }
        });
    };

    const handleTypeChangeApplicant = (value) => {
        setRequestForApplicantId(value.target.value);
    };

    const onChangeOwnerFormik = useFormik({
        initialValues: {
            citizen_id: ''
        },
        validationSchema: FormCompanySendInviteSchema,
        onSubmit: (values) => {
            searchUserById(values.citizen_id);
        }
    });

    const handleTypeChange = (value) => {
        handlePageChange(1);
        setRequestType(value.target.value);
        setRequestName('');
        titleRef.current?.scrollIntoView();
    };

    const handlePageChange = (value) => {
        setCurrentPage(value);
        titleRef.current?.scrollIntoView();
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
    };

    const handleStringFilter = () => {
        handlePageChange(1);
        setStringFilter(requestName);
    };

    const handleRemoveFilters = () => {
        setStringFilter('');
        setRequestName('');
    };

    const handleClaimModalVisibility = () => {
        setSearchUser(null);
        onChangeOwnerFormik.resetForm();
        setClaimModalVisible(!claimModalVisible);
    };

    const deleteFoundUser = () => {
        setSearchUser(null);
        onChangeOwnerFormik.resetForm();
    };

    const handlerChangeOwner = (citizenID) => {
        changeOwner({ request_code: requestCode, new_citizen_id: citizenID, company_id: companySelected });
    };

    const changeOwner = (formData) => {
        mutationChangeMember.mutate(formData, {
            onSuccess: (data) => {
                if (data.success) {
                    Swal.fire({
                        title: 'Información',
                        text: data.msg,
                        icon: 'success'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            setSearchUser(undefined);
                            setRequestCode(undefined);
                            handleClaimModalVisibility();
                        }
                    });
                    // refresh cache of inviteUser
                    queryClient.invalidateQueries('requestsList');
                    queryClient.invalidateQueries('allStatuses');
                } else {
                    Swal.fire({
                        title: 'Información',
                        text: data.msg,
                        icon: 'info'
                    });
                }
            }
        });
    };

    return requestListIsLoading ? <CenterLoading /> : (
        <RequestsWrapper ref={titleRef} style={{ padding: '32px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                {/* Filters Row */}
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '24px', width: '100%', marginTop: '16px' }}>
                    {/* Search filter */}
                    <div style={{ flex: 1, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none' }}>Búsqueda rápida</span>
                        <div style={{ position: 'relative', width: '100%' }}>
                            <TextField
                                placeholder="Nombre o número de solicitud"
                                type="text"
                                id="requestID"
                                value={requestName}
                                onChange={(e) => setRequestName(e.target.value)}
                                style={{ width: '100%' }}
                            />
                            {stringFilter.length > 0 ? (
                                <div style={{ position: 'absolute', zIndex: 10, top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                    <IconButton
                                        onClick={() => handleRemoveFilters()}
                                        aria-label="delete"
                                        size="small"
                                    >
                                        <CloseIcon
                                            titleAccess="Eliminar filtro"
                                            color="error"
                                            sx={{ fontSize: '1.2em' }}
                                        />
                                    </IconButton>
                                </div>
                            ) : (
                                <div style={{ position: 'absolute', zIndex: 10, top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                    <IconButton
                                        onClick={handleStringFilter}
                                        aria-label="search"
                                        size="small"
                                    >
                                        <SearchIcon
                                            titleAccess="buscar"
                                            color="action"
                                            sx={{ fontSize: '1.2em' }}
                                        />
                                    </IconButton>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Applicant filter */}
                    <div style={{ minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none' }}>Filtro por solicitante</span>
                        <Select
                            title=""
                            id="filterSolicitante"
                            data={requestForApplicant.length == 0 ? [finishData] : requestForApplicant}
                            value={requestForApplicantId}
                            onChange={handleTypeChangeApplicant}
                            disableEmptyValue
                        />
                    </div>

                    {/* Status filter */}
                    <div style={{ minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none' }}>Por status</span>
                        <Select
                            title=""
                            id="filterType"
                            data={filteredFilterTypes}
                            value={requestType}
                            onChange={handleTypeChange}
                            disableEmptyValue
                        />
                    </div>
                </div>

                <ListContainer>
                    {requestsList?.data?.map((request, index) => (
                        <Fragment key={request.id}>
                            {index > 0 && <SmallHeightDivider />}
                            <RequestCard
                                title={request.service.name}
                                projectName={request.nmtramite}
                                percent={request.progress + '%'}
                                date={request.created_at}
                                updatedAt={request.updated_at}
                                approvalNumber={request.approval_number}
                                rolBase={rolBase}
                                company_user_rol={request.service.user_rol_company}
                                onClaimModalVisibility={(value) => handleClaimModalVisibility(value)}
                                requestCode={request.code}
                                citizenID={citizenID}
                                status={request.status.name}
                                actionRequired={request.request_actions}
                                requiredActionMessage={request.required_actions?.[0]?.message}
                                docIdentification={request.doc_identification}
                                handleCompanySelected={setCompanySelected}
                                code={request.code}
                                setRequestCode={setRequestCode}
                                companyID={request.company_id}
                                companyName={request.company_name}
                                companyRnc={request.company_rnc}
                                onClick={() => {
                                    const queryParams = new URLSearchParams({
                                        doc_identification: request.doc_identification,
                                        user_rol_company: request.service.user_rol_company,
                                        status_id: request.status.id,
                                        company_id: request.company_id,
                                        company_name: request.company_name
                                    }).toString();

                                    return history.push(`/app/serviceRequestedDetails/${request.id}?${queryParams}`);
                                }}
                                statusID={request.status.id}
                                userName={`${request.user_name || ''} ${request.user_first_last_name || ''} ${request.user_last_name || ''}`.trim()}
                                solution={request.solution}
                                solutionColor={request.status.color}
                            />
                        </Fragment>
                    ))}

                    <MediumHeightDivider />
                    {requestsList?.data?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', width: '100%' }}>
                            <StyledPagination
                                count={requestsList?.last_page}
                                page={currentPage}
                                onChange={(event, page) => {
                                    handlePageChange(page);
                                }}
                                variant="outlined"
                                shape="rounded"
                                sx={{ color: COLORS.primary }}
                            />
                            {totalRequests > 10 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#64748B' }}>
                                    <span>Mostrar:</span>
                                    <select 
                                        value={pageSize} 
                                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #E2E8F0', outline: 'none', backgroundColor: COLORS.white, color: '#000000' }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        {totalRequests > 25 && <option value={50}>50</option>}
                                    </select>
                                </div>
                            )}
                        </div>
                    ) : (
                        <EmptyState
                            title={emptyTitle}
                            subtitle={emptySubtitle}
                            onRefresh={() => {
                                queryClient.invalidateQueries('requestsList');
                                queryClient.invalidateQueries('allStatuses');
                            }}
                        />
                    )}

                    <MediumHeightDivider />
                </ListContainer>
                <DialogChangeApplicant
                    claimModalVisible={claimModalVisible}
                    handleClaimModalVisibility={handleClaimModalVisibility}
                    companySelected={companySelected}
                    searchUser={searchUser}
                    deleteFoundUser={deleteFoundUser}
                    onClick={handlerChangeOwner}
                    title="Cambiar propietario"
                    sendButtonLabel={'ENVIAR'}
                />
                </div>
        </RequestsWrapper>
    );
}

export default MyRequests;
